import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, EMPTY, merge, Observable, Subject, Subscription, throwError, timer } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '@app/shared/auth/auth.service';
import { BillingExport } from '@app/shared/models/billing-export';
import { ReportsService } from '../api/reports.service';
import { BlobDownloadService } from './blob-download.service';

@Injectable({ providedIn: 'root' })
export class BillingExportsService implements OnDestroy {
  private readonly items = new BehaviorSubject<BillingExport[]>([]);
  readonly items$ = this.items.asObservable();
  private readonly refreshError = new BehaviorSubject(false);
  readonly refreshError$ = this.refreshError.asObservable();
  private readonly refreshRequested = new Subject<void>();
  private readonly scopeChanged = new Subject<void>();
  private readonly notified = new Set<string>();
  private readonly activeToasts = new Set<number>();
  private monitor?: Subscription;
  private clients = 0;

  constructor(private reports: ReportsService, private auth: AuthService,
    private toastr: ToastrService, private router: Router, private downloads: BlobDownloadService) {}

  connect(): void {
    if (++this.clients !== 1) { return; }
    this.monitor = combineLatest([
      this.auth.authenticationState, this.auth.patientEntryKioskMode$,
      this.auth.appLocked$, this.auth.patientFormLocked$,
      this.auth.clinicChanged.pipe(startWith(null)), this.auth.labChanged.pipe(startWith(null))
    ]).pipe(
      map(() => this.canUseExports()
        ? JSON.stringify([this.auth.getUserData('account_id'), this.auth.getUserData('login_id'),
          this.auth.getUserData('id'), this.auth.getUserData('login_type')]) : null),
      distinctUntilChanged(),
      switchMap(scope => {
        this.reset();
        if (!scope) { return EMPTY; }
        return merge(timer(0, 5000), this.refreshRequested).pipe(
          exhaustMap(() => {
            const idsAtRequest = new Set(this.items.value.map(item => item.id));
            return this.reports.getBillingExports().pipe(
              tap(items => {
                // An older poll can finish after a new request is accepted.
                // Keep that new export visible until the next poll includes it.
                const receivedIds = new Set(items.map(item => item.id));
                const acceptedMeanwhile = this.items.value.filter(item =>
                  !idsAtRequest.has(item.id) && !receivedIds.has(item.id));
                this.refreshError.next(false);
                this.accept([...acceptedMeanwhile, ...items]);
              }),
              catchError(() => { this.refreshError.next(true); return EMPTY; })
            );
          })
        );
      })
    ).subscribe();
  }

  disconnect(): void {
    this.clients = Math.max(0, this.clients - 1);
    if (this.clients === 0) {
      this.monitor?.unsubscribe();
      this.monitor = undefined;
      this.reset();
    }
  }

  refresh(): void { this.refreshRequested.next(); }

  create(options: any): Observable<BillingExport> {
    if (!this.canUseExports()) { return throwError(() => new Error('Billing exports are unavailable in this session.')); }
    return this.reports.getBillingToExport(options).pipe(
      takeUntil(this.scopeChanged),
      tap(item => {
        this.items.next([item, ...this.items.value.filter(previous => previous.id !== item.id)]);
        this.refresh();
      })
    );
  }

  download(item: BillingExport): void {
    if (!this.canUseExports()) { return; }
    this.reports.getBillingExportDownloadLink(item.id).pipe(
      takeUntil(this.scopeChanged),
      tap(({ url }) => this.downloads.downloadUrl(url, 'billing-report.' + item.format))
    ).subscribe({
      error: () => {
        this.toastr.error('This file may have expired. Refresh your downloads or generate a new export.', 'Download unavailable');
        this.refresh();
      }
    });
  }

  private accept(items: BillingExport[]): void {
    const newlyReady = items.filter(item => item.status === 'completed' && !this.notified.has(item.id));
    this.items.next(items);
    if (!newlyReady.length) { return; }
    newlyReady.forEach(item => this.notified.add(item.id));
    const message = newlyReady.length === 1 ? 'Your billing export is ready. Click to download.'
      : `${newlyReady.length} billing exports are ready. Click to view downloads.`;
    const toast = this.toastr.success(message, 'Billing export ready', { timeOut: 10000, closeButton: true });
    this.activeToasts.add(toast.toastId);
    toast.onHidden?.pipe(take(1)).subscribe(() => this.activeToasts.delete(toast.toastId));
    toast.onTap.pipe(
      take(1), takeUntil(this.scopeChanged)
    ).subscribe(() => {
      if (newlyReady.length === 1) { this.download(newlyReady[0]); }
      else { this.router.navigate(['/manage/reports/billings'], { fragment: 'billing-downloads' }); }
    });
  }

  private reset(): void {
    this.scopeChanged.next();
    this.activeToasts.forEach(id => this.toastr.clear(id));
    this.activeToasts.clear();
    this.items.next([]);
    this.refreshError.next(false);
    this.notified.clear();
  }

  private canUseExports(): boolean {
    return this.auth.authenticationState.value && !this.auth.patientEntryKioskMode
      && !this.auth.patientFormLocked && !this.auth.isAppLocked()
      && ![true, 1, '1'].includes(this.auth.getUserData('kiosk'));
  }

  ngOnDestroy(): void {
    this.monitor?.unsubscribe();
    this.reset();
    this.scopeChanged.complete();
    this.refreshRequested.complete();
  }
}
